import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronRight,
  Calendar,
  Package,
  User,
  FileText,
  RefreshCw,
  Loader2,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== Stat Card ====================
const StatCard = ({ label, value, subValue, icon: Icon, variant = 'default' }) => {
  const variants = {
    default: 'bg-slate-50 border-slate-200',
    breakage: 'bg-orange-50 border-orange-200',
    consumption: 'bg-blue-50 border-blue-200',
  };
  
  const iconColors = {
    default: 'text-slate-500',
    breakage: 'text-orange-500',
    consumption: 'text-blue-500',
  };
  
  return (
    <div className={`p-4 rounded-lg border ${variants[variant]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${iconColors[variant]}`} />}
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
    </div>
  );
};

// ==================== History Row ====================
const HistoryRow = ({ occurrence, onClick, expanded, onToggle }) => {
  const isConsumption = occurrence.movement_type === 'consumption';
  const isPosted = occurrence.status === 'posted' || occurrence.status === 'sage_posted';
  const isUnconfirmed = occurrence.status === 'unconfirmed';
  const isFailed = occurrence.status === 'failed';
  const isPending = occurrence.status === 'pending';
  
  const lines = occurrence.lines || [];
  
  const getStatusBadge = () => {
    if (isPosted) return <Badge className="bg-emerald-100 text-emerald-700 text-xs">Integrado</Badge>;
    if (isUnconfirmed) return <Badge className="bg-amber-100 text-amber-700 text-xs">Por Confirmar</Badge>;
    if (isFailed) return <Badge className="bg-red-100 text-red-700 text-xs">Falhou</Badge>;
    if (isPending) return <Badge className="bg-slate-100 text-slate-700 text-xs">Pendente</Badge>;
    if (occurrence.status === 'cancelled') return <Badge className="bg-slate-100 text-slate-500 text-xs">Cancelado</Badge>;
    return <Badge className="bg-slate-100 text-slate-700 text-xs">{occurrence.status}</Badge>;
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }) + 
           ' ' + d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid={`history-row-${occurrence.id}`}>
      <div 
        className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50"
        onClick={() => onToggle(occurrence.id)}
      >
        <button className="shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-6 gap-2 sm:gap-4 items-center">
          {/* Date */}
          <div className="text-sm text-slate-600">
            {formatDate(occurrence.created_at)}
          </div>
          
          {/* Type */}
          <div>
            <Badge variant="outline" className={isConsumption ? 'border-blue-300 text-blue-700' : 'border-orange-300 text-orange-700'}>
              {isConsumption ? 'Consumo' : 'Quebra'}
            </Badge>
          </div>
          
          {/* Responsible */}
          <div className="flex items-center gap-1 text-sm truncate">
            <User className="h-3 w-3 text-slate-400 shrink-0" />
            <span className="truncate">{occurrence.responsible_name || 'N/A'}</span>
          </div>
          
          {/* Reason */}
          <div className="text-sm text-slate-600 truncate">
            {occurrence.reason_label || occurrence.reason}
          </div>
          
          {/* Items/Units */}
          <div className="text-sm">
            <span className="font-medium">{occurrence.total_lines || 1}</span>
            <span className="text-slate-500"> artigos · </span>
            <span className="font-medium">{occurrence.total_units || occurrence.quantity || 0}</span>
            <span className="text-slate-500"> un.</span>
          </div>
          
          {/* Status / SAGE */}
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            {occurrence.sage_doc_reference && (
              <span className="text-xs font-mono text-green-600 hidden lg:inline">
                {occurrence.sage_doc_reference}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Detail */}
      {expanded && (
        <div className="border-t bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-xs">Armazém</p>
              <p className="font-medium">{occurrence.store_name || occurrence.warehouse_code}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Registado por</p>
              <p className="font-medium">{occurrence.created_by_name || occurrence.user_name}</p>
            </div>
            {occurrence.observation && (
              <div className="col-span-2">
                <p className="text-slate-500 text-xs">Observação</p>
                <p className="font-medium">{occurrence.observation}</p>
              </div>
            )}
            {occurrence.sage_doc_reference && (
              <div>
                <p className="text-slate-500 text-xs">Documento SAGE</p>
                <p className="font-medium font-mono text-emerald-600">{occurrence.sage_doc_reference}</p>
              </div>
            )}
            
            {/* Error info for failed/unconfirmed */}
            {isFailed && (
              <div className="col-span-2 p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-xs font-medium text-red-800 mb-1">Falhou a criar documento SAGE</p>
                <p className="text-xs text-red-700">
                  {occurrence.error_message?.includes('quantidade suficiente') || occurrence.error_message?.includes('stock insuficiente')
                    ? 'Stock insuficiente no armazém para esta quantidade.'
                    : occurrence.error_message?.includes('terceiro') || occurrence.error_message?.includes('preço')
                    ? 'Erro de configuração no SAGE.'
                    : 'Ocorreu um erro na integração.'}
                </p>
              </div>
            )}
            {isUnconfirmed && (
              <div className="col-span-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs font-medium text-amber-800 mb-1">Por confirmar no SAGE</p>
                <p className="text-xs text-amber-700">
                  Não foi possível confirmar se o documento foi criado. Use "Confirmar no SAGE" para verificar.
                </p>
              </div>
            )}
          </div>
          
          {/* Lines */}
          {lines.length > 0 && (
            <div>
              <p className="text-slate-500 text-xs mb-2">Artigos ({lines.length})</p>
              <div className="space-y-1">
                {lines.map((ln, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{ln.sku}</p>
                      <p className="text-xs text-slate-500 truncate">{ln.product_name}</p>
                    </div>
                    <Badge variant="secondary">{ln.quantity}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Action button for failed/unconfirmed */}
          {(isFailed || isUnconfirmed) && onClick && (
            <div className="pt-2 border-t">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={(e) => { e.stopPropagation(); onClick(occurrence); }}
                className="w-full sm:w-auto"
              >
                Ver detalhes e opções →
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== Analysis Section ====================
const AnalysisSection = ({ stats }) => {
  if (!stats) return null;
  
  const topResponsibles = stats.top_responsibles || [];
  const topReasons = stats.top_reasons || [];
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Análise
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Top Responsibles */}
        {topResponsibles.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Por Responsável</p>
            <div className="space-y-2">
              {topResponsibles.slice(0, 5).map((r, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="truncate flex-1">{r.name}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{r.count} oc.</Badge>
                    <Badge variant="secondary" className="text-xs">{r.units} un.</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Top Reasons */}
        {topReasons.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Por Motivo</p>
            <div className="space-y-2">
              {topReasons.slice(0, 5).map((r, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="truncate flex-1">{r.label}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{r.count} oc.</Badge>
                    <Badge variant="secondary" className="text-xs">{r.units} un.</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== Main Component ====================
export default function BreakageHistory() {
  const navigate = useNavigate();
  
  // Auth
  const [user, setUser] = useState(null);
  const getToken = useCallback(() => localStorage.getItem('token'), []);
  
  // Filters
  const [filters, setFilters] = useState({
    movement_type: '',
    responsible_name: '',
    reason: '',
    sku: '',
    status: '',
    date_from: '',
    date_to: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [reasons, setReasons] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 25;
  
  // Expanded rows
  const [expandedId, setExpandedId] = useState(null);
  
  // Handle click on occurrence for detail view
  const handleOccurrenceClick = (occurrence) => {
    // Navigate to breakage page with occurrence id in query to open modal
    navigate(`/breakage?detail=${occurrence.id}`);
  };
  
  // Load user
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch (e) {}
    }
  }, []);
  
  // Load reasons
  useEffect(() => {
    const loadReasons = async () => {
      const token = getToken();
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/api/breakage/reasons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReasons(response.data.reasons || []);
      } catch (err) {
        console.error('Failed to load reasons:', err);
      }
    };
    
    loadReasons();
  }, [getToken]);
  
  // Load history
  const loadHistory = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', pageSize.toString());
      params.append('skip', (page * pageSize).toString());
      
      if (filters.movement_type) params.append('movement_type', filters.movement_type);
      if (filters.responsible_name) params.append('responsible_name', filters.responsible_name);
      if (filters.reason) params.append('reason', filters.reason);
      if (filters.sku) params.append('sku', filters.sku);
      if (filters.status) params.append('status', filters.status);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await axios.get(`${API}/api/breakage/history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken, page, filters]);
  
  // Load stats
  const loadStats = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await axios.get(`${API}/api/breakage/history/stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [getToken, filters.date_from, filters.date_to]);
  
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);
  
  useEffect(() => {
    loadStats();
  }, [loadStats]);
  
  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };
  
  const clearFilters = () => {
    setFilters({
      movement_type: '',
      responsible_name: '',
      reason: '',
      sku: '',
      status: '',
      date_from: '',
      date_to: '',
    });
    setPage(0);
  };
  
  const hasActiveFilters = Object.values(filters).some(v => v);
  
  const totalPages = Math.ceil(total / pageSize);
  
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/breakage')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Histórico de Quebras e Consumos</h1>
            <p className="text-slate-500 text-sm mt-1">
              Consulte e analise os registos anteriores
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => { loadHistory(); loadStats(); }}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
        
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Total Ocorrências"
              value={stats.total_occurrences || 0}
              icon={FileText}
            />
            <StatCard
              label="Quebras"
              value={stats.total_breakages || 0}
              subValue={`${stats.units_breakages || 0} unidades`}
              icon={AlertTriangle}
              variant="breakage"
            />
            <StatCard
              label="Consumos"
              value={stats.total_consumptions || 0}
              subValue={`${stats.units_consumptions || 0} unidades`}
              icon={Package}
              variant="consumption"
            />
            <StatCard
              label="Total Unidades"
              value={(stats.units_breakages || 0) + (stats.units_consumptions || 0)}
              icon={TrendingDown}
            />
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-4">
            {/* Filters Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={showFilters ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtros
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-primary-foreground text-primary">
                    {Object.values(filters).filter(v => v).length}
                  </Badge>
                )}
              </Button>
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Limpar
                </Button>
              )}
              
              <div className="flex-1" />
              
              <span className="text-sm text-slate-500">
                {total} resultado{total !== 1 ? 's' : ''}
              </span>
            </div>
            
            {/* Filters Panel */}
            {showFilters && (
              <Card>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Movement Type */}
                    <div>
                      <Label className="text-xs">Tipo</Label>
                      <Select 
                        value={filters.movement_type} 
                        onValueChange={(v) => handleFilterChange('movement_type', v)}
                      >
                        <SelectTrigger data-testid="filter-type">
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Todos</SelectItem>
                          <SelectItem value="breakage">Quebra</SelectItem>
                          <SelectItem value="consumption">Consumo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Responsible */}
                    <div>
                      <Label className="text-xs">Responsável</Label>
                      <Input
                        value={filters.responsible_name}
                        onChange={(e) => handleFilterChange('responsible_name', e.target.value)}
                        placeholder="Pesquisar..."
                        data-testid="filter-responsible"
                      />
                    </div>
                    
                    {/* Reason */}
                    <div>
                      <Label className="text-xs">Motivo</Label>
                      <Select 
                        value={filters.reason} 
                        onValueChange={(v) => handleFilterChange('reason', v)}
                      >
                        <SelectTrigger data-testid="filter-reason">
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Todos</SelectItem>
                          {reasons.map(r => (
                            <SelectItem key={r.code} value={r.code}>
                              {r.label_pt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* SKU */}
                    <div>
                      <Label className="text-xs">SKU / Produto</Label>
                      <Input
                        value={filters.sku}
                        onChange={(e) => handleFilterChange('sku', e.target.value)}
                        placeholder="Pesquisar..."
                        data-testid="filter-sku"
                      />
                    </div>
                    
                    {/* Status */}
                    <div>
                      <Label className="text-xs">Estado SAGE</Label>
                      <Select 
                        value={filters.status || 'all'} 
                        onValueChange={(v) => handleFilterChange('status', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger data-testid="filter-status">
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="posted">Integrado SAGE</SelectItem>
                          <SelectItem value="pending">Pendente</SelectItem>
                          <SelectItem value="unconfirmed">Por Confirmar</SelectItem>
                          <SelectItem value="failed">Falhou</SelectItem>
                          <SelectItem value="cancelled">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Date Range */}
                    <div className="sm:col-span-2 lg:col-span-1 grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">De</Label>
                        <Input
                          type="date"
                          value={filters.date_from}
                          onChange={(e) => handleFilterChange('date_from', e.target.value)}
                          data-testid="filter-date-from"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Até</Label>
                        <Input
                          type="date"
                          value={filters.date_to}
                          onChange={(e) => handleFilterChange('date_to', e.target.value)}
                          data-testid="filter-date-to"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Results */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : items.length > 0 ? (
              <div className="space-y-2">
                {items.map(occ => (
                  <HistoryRow
                    key={occ.id}
                    occurrence={occ}
                    expanded={expandedId === occ.id}
                    onToggle={(id) => setExpandedId(expandedId === id ? null : id)}
                    onClick={handleOccurrenceClick}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="font-medium">Nenhum registo encontrado</p>
                <p className="text-sm mt-1">Ajuste os filtros ou verifique as permissões</p>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Anterior
                </Button>
                <span className="text-sm text-slate-500">
                  Página {page + 1} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Seguinte
                </Button>
              </div>
            )}
          </div>
          
          {/* Sidebar - Analysis */}
          <div className="space-y-4">
            <AnalysisSection stats={stats} />
          </div>
        </div>
      </div>
    </div>
  );
}
